import { createStore, useStore } from "zustand";
import type { Pane, PaneType } from "./types";

let uidCounter = 0;
function nextUid(): string {
    return `pane-${uidCounter++}`;
}

/**
 * 移動の履歴。訪問した順に積んだペインの `uid` と、いま何段目を見ているかのカーソル。
 * 規則は [[2026-08-18 設計ノート アドイン 画面ナビゲーションと履歴]] が所有する。
 * 空のときカーソルは -1。
 */
interface History {
    entries: string[];
    index: number;
}

/**
 * 移った。いまカーソルが指す段と同じペインなら何もしない。
 * 違うなら、カーソルより先を捨ててから末尾へ積み、カーソルを末尾へ動かす。
 */
function visited(history: History, uid: string): History {
    if (history.entries[history.index] === uid) return history;
    const entries = [...history.entries.slice(0, history.index + 1), uid];
    return { entries, index: entries.length - 1 };
}

/**
 * 消えた。そのペインの段を履歴から全部抜き、抜いた数だけカーソルを手前へ詰める。
 * 詰めた結果、同じ段が隣り合ったら 1 つにまとめる（戻るを押しても画面が変わらない段を残さない）。
 */
function removed(history: History, uid: string): History {
    const entries: string[] = [];
    let index = history.index;
    history.entries.forEach((entry, i) => {
        if (entry === uid) {
            if (i <= history.index) index -= 1;
            return;
        }
        entries.push(entry);
    });
    return collapseAdjacent({
        entries,
        index: entries.length === 0 ? -1 : Math.max(index, 0),
    });
}

/** 隣り合った同じ段を 1 つにまとめ、カーソルをまとめた後の段へ寄せる。 */
function collapseAdjacent(history: History): History {
    const entries: string[] = [];
    let index = history.index;
    history.entries.forEach((entry, i) => {
        if (entries[entries.length - 1] === entry) {
            if (i <= history.index) index -= 1;
            return;
        }
        entries.push(entry);
    });
    return { entries, index };
}

/**
 * ペインを1つ取り除いた後の panes・activePaneUid・履歴を求める。
 * 着地は**履歴のカーソルが指す段**で決める（作成順では決めない）。履歴が空になったらホームへ着地し、
 * その着地も 1 段として積む（カーソルは常にアクティブなペインを指す）。
 * closePane（手動クローズ）と removePaneByResourceId（削除）で共有する。
 */
function afterRemoval(
    state: PaneManagerState,
    removedUid: string
): Pick<PaneManagerState, "panes" | "activePaneUid" | "history" | "historyIndex"> {
    const panes = state.panes.filter((p) => p.uid !== removedUid);
    let history = removed({ entries: state.history, index: state.historyIndex }, removedUid);
    let activePaneUid = history.entries[history.index];
    if (!activePaneUid) {
        const home = panes.find((p) => p.type === "home") ?? panes[0];
        activePaneUid = home?.uid ?? state.activePaneUid;
        history = visited(history, activePaneUid);
    }
    return { panes, activePaneUid, history: history.entries, historyIndex: history.index };
}

/** 移った先を履歴へ積み、そこをアクティブにする。 */
function moveTo(state: PaneManagerState, uid: string) {
    const history = visited({ entries: state.history, index: state.historyIndex }, uid);
    return {
        activePaneUid: uid,
        history: history.entries,
        historyIndex: history.index,
    };
}

interface PaneManagerState {
    panes: Pane[];
    activePaneUid: string;
    history: string[];
    historyIndex: number;
}

interface PaneManagerActions {
    openPane(type: PaneType, id?: string): void;
    closePane(uid: string): void;
    switchPane(uid: string): void;
    goBack(): void;
    goForward(): void;
    goToHome(): void;
    removePaneByResourceId(id: string): void;
}

type PaneManagerStore = PaneManagerState & PaneManagerActions;

const homePane: Pane = { uid: nextUid(), type: "home" };

export const paneManagerStore = createStore<PaneManagerStore>((set, get) => ({
    panes: [homePane],
    activePaneUid: homePane.uid,
    history: [homePane.uid],
    historyIndex: 0,

    openPane(type, id) {
        const state = get();
        // 生成担当だけが同種で複数開く。ほかは種別ごとに1つで、既にあればそこへ切り替える。
        const existing =
            type === "assignment"
                ? id !== undefined &&
                  state.panes.find((p) => p.type === type && "id" in p && p.id === id)
                : state.panes.find((p) => p.type === type);
        // 既にあるペインへの切り替えも履歴へ積む（積まないと往復が辿れず、戻るが効かない）。
        if (existing) {
            set(moveTo(state, existing.uid));
            return;
        }
        const uid = nextUid();
        const pane: Pane =
            type === "assignment" ? ({ uid, type, id: id! } as Pane) : ({ uid, type } as Pane);
        set({ panes: [...state.panes, pane], ...moveTo(state, uid) });
    },

    closePane(uid) {
        const state = get();
        const target = state.panes.find((p) => p.uid === uid);
        if (!target || target.type === "home") return;
        // 編集境界での空ノード掃除。ペインを閉じる時点でノードも同時に取り除かれるため、
        // 実質空なら掃除しても矛盾しない（切替経路では掃除しない＝再訪時の「見つかりません」防止）。
        // 下書き（ラベルが空）の担当はストアに実体が無いので掃除は要らない。
        // 明示削除は各ペインが行う。
        set(afterRemoval(state, uid));
    },

    switchPane(uid) {
        set(moveTo(get(), uid));
    },

    goBack() {
        const { history, historyIndex } = get();
        if (historyIndex <= 0) return;
        const index = historyIndex - 1;
        set({ historyIndex: index, activePaneUid: history[index] });
    },

    goForward() {
        const { history, historyIndex } = get();
        if (historyIndex >= history.length - 1) return;
        const index = historyIndex + 1;
        set({ historyIndex: index, activePaneUid: history[index] });
    },

    goToHome() {
        const state = get();
        const home = state.panes.find((p) => p.type === "home");
        if (home) {
            set(moveTo(state, home.uid));
        }
    },

    removePaneByResourceId(id) {
        const state = get();
        const target = state.panes.find((p) => "id" in p && p.id === id);
        if (!target) return;
        set(afterRemoval(state, target.uid));
    },
}));

export function usePaneManager<T>(selector: (s: PaneManagerStore) => T): T {
    return useStore(paneManagerStore, selector);
}

/** 戻れるか。ヘッダーの戻るを押せない見た目にするために使う。 */
export function canGoBack(s: { historyIndex: number }): boolean {
    return s.historyIndex > 0;
}

/** 進めるか。ヘッダーの進むを押せない見た目にするために使う。 */
export function canGoForward(s: { history: string[]; historyIndex: number }): boolean {
    return s.historyIndex < s.history.length - 1;
}
